import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import { API } from "@/context/AuthContext";
import CategoriesPanel from "./financial/CategoriesPanel";
import FinancialProductsPanel from "./financial/FinancialProductsPanel";
import TaxonomyCrudPanel from "./financial/TaxonomyCrudPanel";

const SUB_TABS = ["categories", "products", "product-categories", "product-subcategories"];

/**
 * Configuración Financiera — admin-only sub-module.
 * Two sub-tabs: Categories and Financial Products.
 * Strictly additive: NOT connected to orders / profit calculation yet.
 */
export default function FinancialConfigPanel({ onBack }) {
  const { t } = useI18n();
  const [sub, setSub] = useState("categories");
  // Used to refresh products when categories change (e.g. activate/deactivate).
  const [categoriesVersion, setCategoriesVersion] = useState(0);
  const [productCategories, setProductCategories] = useState([]);

  const loadProductCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/product-categories`, {
        withCredentials: true,
      });
      setProductCategories(Array.isArray(data) ? data : []);
    } catch {
      setProductCategories([]);
    }
  }, []);

  useEffect(() => {
    if (sub === "product-categories" || sub === "product-subcategories") {
      loadProductCategories();
    }
  }, [sub, loadProductCategories]);

  return (
    <div data-testid={DASHBOARD.financialConfigRoot} className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            data-testid={DASHBOARD.financialBackBtn}
            onClick={onBack}
            className="font-mono-tech text-xs text-[var(--text-secondary)] hover:text-black mb-2"
          >
            {t("back_to_settings")}
          </button>
          <div className="label-overline mb-1">SET / 01</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
            {t("financial_config_title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {t("financial_config_subtitle")}
          </p>
        </div>
      </div>

      {/* Sub-tabs */}
      <nav className="flex gap-1 border-b border-[var(--border)]">
        {SUB_TABS.map((k) => {
          const active = sub === k;
          const ids = {
            categories: DASHBOARD.tabCategories,
            products: DASHBOARD.tabFinancialProducts,
            "product-categories": DASHBOARD.tabProductCategories,
            "product-subcategories": DASHBOARD.tabProductSubcategories,
          };
          const labels = {
            categories: t("tab_categories"),
            products: t("tab_financial_products"),
            "product-categories": t("tab_product_categories"),
            "product-subcategories": t("tab_product_subcategories"),
          };
          return (
            <button
              key={k}
              data-testid={ids[k]}
              onClick={() => setSub(k)}
              className={`px-4 py-3 font-display font-bold text-sm tracking-wide border-b-2 -mb-px transition-colors ${
                active
                  ? "border-black text-black"
                  : "border-transparent text-[var(--text-secondary)] hover:text-black"
              }`}
            >
              {labels[k]}
            </button>
          );
        })}
      </nav>

      {sub === "categories" && (
        <CategoriesPanel onCategoriesChanged={() => setCategoriesVersion((v) => v + 1)} />
      )}
      {sub === "products" && (
        <FinancialProductsPanel categoriesVersion={categoriesVersion} />
      )}
      {sub === "product-categories" && (
        <TaxonomyCrudPanel
          entity="category"
          testIdPrefix={DASHBOARD.productCategoriesPrefix}
          parentCategories={productCategories}
        />
      )}
      {sub === "product-subcategories" && (
        <TaxonomyCrudPanel
          entity="subcategory"
          testIdPrefix={DASHBOARD.productSubcategoriesPrefix}
          parentCategories={productCategories}
        />
      )}
    </div>
  );
}
